import { getCdfDisplayTitle } from 'features/dataLayers'
import { type GeckoComponentDataFrame, GeckoMeasurementUnit } from 'types'

export interface AssetVizResolutionOption {
  cdf: GeckoComponentDataFrame
  label: string
  key: string
}

export function getCdfResolutionOptions({
  cdfs,
  selectedCDF,
  getPreferredMeasurementUnit,
}: {
  cdfs: readonly GeckoComponentDataFrame[]
  selectedCDF: GeckoComponentDataFrame | undefined
  getPreferredMeasurementUnit: (
    unit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
}): AssetVizResolutionOption[] {
  if (!selectedCDF) return []

  const selectedLayerLabel = getCdfDisplayTitle(
    selectedCDF,
    getPreferredMeasurementUnit(selectedCDF.data.params.measurement_unit)
  )
  const seen = new Set<string>()
  const candidates = [selectedCDF, ...cdfs]

  return candidates
    .filter(
      (cdf) =>
        cdf.tags.component === selectedCDF.tags.component &&
        cdf.tags.inspection === selectedCDF.tags.inspection &&
        getCdfDisplayTitle(
          cdf,
          getPreferredMeasurementUnit(cdf.data.params.measurement_unit)
        ) === selectedLayerLabel
    )
    .toSorted((left, right) => getBinAreaMm(left) - getBinAreaMm(right))
    .map((cdf) => ({
      cdf,
      label: formatCdfResolutionLabel(cdf, getPreferredMeasurementUnit),
      key: getCdfResolutionKey(cdf),
    }))
    .filter((option) =>
      seen.has(option.key) ? false : (seen.add(option.key), true)
    )
}

/**
 * Pick the coarsest (lowest-definition / largest bin area) CDF within the
 * resolution family of `selectedCDF`. Reuses `getCdfResolutionOptions`, which
 * filters to the same component/inspection/display-title and sorts finest-first
 * by bin area, so the coarsest is simply the last option. Falls back to
 * `selectedCDF` when there are no options (e.g. `selectedCDF` is undefined or
 * has no family peers).
 *
 * Used by the libra FFS viz to default to a light, low-density resolution so the
 * viewer doesn't open on a high-density bin that can lag the user's machine.
 */
export function getCoarsestCdf(args: {
  cdfs: readonly GeckoComponentDataFrame[]
  selectedCDF: GeckoComponentDataFrame | undefined
  getPreferredMeasurementUnit: (
    unit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
}): GeckoComponentDataFrame | undefined {
  const options = getCdfResolutionOptions(args)
  return options.at(-1)?.cdf ?? args.selectedCDF
}

function getCdfResolutionKey(cdf: GeckoComponentDataFrame): string {
  const width = getResolutionMeasurement(cdf.data.binning.bin_size.width)
  const height = getResolutionMeasurement(cdf.data.binning.bin_size.height)

  return `${width.num.toPrecision(12)}:${width.unit.id}:${height.num.toPrecision(
    12
  )}:${height.unit.id}`
}

function formatCdfResolutionLabel(
  cdf: GeckoComponentDataFrame,
  getPreferredMeasurementUnit: (
    unit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
): string {
  const width = getResolutionMeasurement(
    cdf.data.binning.bin_size.width,
    getPreferredMeasurementUnit
  )
  const height = getResolutionMeasurement(
    cdf.data.binning.bin_size.height,
    getPreferredMeasurementUnit
  )
  const formattedWidth = formatResolutionNumber(width.num)
  const formattedHeight = formatResolutionNumber(height.num)
  const unitLabel =
    width.unit === height.unit
      ? width.unit.name
      : `${width.unit.name} x ${height.unit.name}`

  return formattedWidth === formattedHeight
    ? `${formattedWidth} ${unitLabel}`
    : `${formattedWidth} x ${formattedHeight} ${unitLabel}`
}

function getBinAreaMm(cdf: GeckoComponentDataFrame): number {
  const width = getResolutionMeasurement(cdf.data.binning.bin_size.width)
  const height = getResolutionMeasurement(cdf.data.binning.bin_size.height)

  return width.num * height.num
}

function formatResolutionNumber(value: number): string {
  if (value >= 10) return value.toFixed(value % 1 === 0 ? 0 : 1)
  if (value >= 1) return value.toFixed(1).replace(/\.0$/, '')
  return value.toPrecision(2)
}

function getResolutionMeasurement(
  measurement: GeckoComponentDataFrame['data']['binning']['bin_size']['width'],
  getPreferredMeasurementUnit: (
    unit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit = () => GeckoMeasurementUnit.Millimeters
) {
  return isLengthResolutionUnit(measurement.unit)
    ? measurement.convertTo(getPreferredMeasurementUnit(measurement.unit))
    : measurement
}

function isLengthResolutionUnit(unit: GeckoMeasurementUnit): boolean {
  return [
    GeckoMeasurementUnit.Inches,
    GeckoMeasurementUnit.Feet,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Millimeters,
  ].includes(unit)
}
